export type NotificationScheduleMode = 'immediate' | 'interval' | 'daily' | 'weekly' | 'monthly';
export type NotificationSeverity = 'info' | 'warning' | 'error';
export type NotificationChannel = 'in_app' | 'email';
export type NotificationEventClass = 'transactional' | 'informational';

export const NOTIFICATION_SEVERITIES: readonly NotificationSeverity[] = ['info', 'warning', 'error'];

/** A subscriber's timing. Only the fields the mode uses are read; the rest keep their defaults. */
export class NotificationSchedule {
  public days: string[] = ['mon'];
  public everyHours: number = 6;
  public mode: NotificationScheduleMode = 'immediate';
  public monthDay: number = 1;
  public times: string[] = ['08:00'];
}

/** One suite product as the catalog presents it (spec §7.1). inPlan false = locked, still rendered. */
export class NotificationProduct {
  public blurb: string = '';
  public icon: string = 'bell';
  public inPlan: boolean = true;
  public key: string = 'comply';
  public label: string = '4Comply';
  /**
   * 'unknown' when the API could not check the subscription at all, undefined otherwise. A locked tab
   * with this set says "could not be checked" rather than showing an upsell for a product the tenant
   * may already own (API plan Codex 3.2).
   */
  public planState?: string;
  public tint: string = '#8c1d18';
}

/** One event of GET /v1/notifications/catalog (spec §7.4). */
export class NotificationCatalogEntry {
  public allowsImmediate: boolean = true;
  public allowsPeriodic: boolean = false;
  public class: NotificationEventClass = 'informational';
  public description: string = '';
  public eventType: string = '';
  public hasLookAhead: boolean = false;
  public hasSeverity: boolean = false;
  public hasThreshold: boolean = false;
  public label: string = '';
  /** The slice label when no producer exists yet ("Slice 5"); undefined when the event is live. */
  public planned?: string;
  public product: string = 'comply';
}

/** The whole catalog response: the tab strip plus the rows. */
export class NotificationCatalog {
  public events: NotificationCatalogEntry[] = [];
  public products: NotificationProduct[] = [];
}

/**
 * GET /v1/notifications/parameters — every option set the popover and condition row render (spec §7.3).
 *
 * Deliberately **empty by default**. An earlier revision shipped the API's sets as fallbacks, which
 * contradicted this plan's own constraint never to hard-code them: a fallback that disagrees with the
 * deployed sanitizer is exactly the drift the endpoint exists to prevent (Codex 4.1). When the call
 * fails, the schedule and condition controls render read-only and say so.
 */
export class NotificationParameters {
  public intervalHours: number[] = [];
  public lookAheadDays: number[] = [];
  public maxTimesPerDay: number = 0;
  public minCounts: number[] = [];
  public monthDays: number[] = [];
  public scheduleModes: NotificationScheduleMode[] = [];
  public severities: NotificationSeverity[] = [];
  public weekdays: string[] = [];

  /** False when /parameters did not answer; the tab renders its timing controls read-only. */
  public get loaded(): boolean {
    return this.scheduleModes.length > 0;
  }
}
